/**
 * batch-66 interne-geldstroom (FO-494) - inkoopfacturen koppelen aan de interne
 * geldstroom (BV -> BV).
 *
 * Eén record (`InterneGeldstroom`) verbindt drie lagen die tot nu toe los van
 * elkaar stonden:
 *   - de KOSTENKANT: de inkoopfactuur bij de ontvangende BV;
 *   - de OMZETKANT: de spiegel-verkoopfactuur bij de factuurende (bron-)BV;
 *   - de ENGINE: de intercompany-doorbelasting(en) die ermee zijn afgerekend.
 *
 * Dit bestand is de gedeelde bron voor de API, de backoffice-formulieren en het
 * opschoonscript: statussen + labels, de request-schema's (het form valideert
 * er client-side mee vóór verzenden) en de DTO's. Bedragen reizen als string met
 * twee decimalen (`"5000.00"`), nooit als float; de UI formatteert met `Intl`.
 */

import { z } from 'zod'

// ─── Statussen, oorsprong, verschil ──────────────────────────────────────────

export const INTERNE_GELDSTROOM_STATUS = ['OPEN', 'GEKOPPELD', 'AFGEREKEND', 'VERSCHIL'] as const
export type InterneGeldstroomStatus = (typeof INTERNE_GELDSTROOM_STATUS)[number]

export const INTERNE_GELDSTROOM_STATUS_LABELS: Record<InterneGeldstroomStatus, string> = {
  OPEN: 'Eenzijdig',
  GEKOPPELD: 'Gekoppeld',
  AFGEREKEND: 'Afgerekend',
  VERSCHIL: 'Verschil',
}

/** Uitleg per status (tooltip / legenda). */
export const INTERNE_GELDSTROOM_STATUS_UITLEG: Record<InterneGeldstroomStatus, string> = {
  OPEN: 'Eén kant ontbreekt nog: de spiegel-verkoopfactuur of de inkoopfactuur.',
  GEKOPPELD: 'De inkoop- en de verkoopfactuur verwijzen naar elkaar. Nog niet afgerekend met de doorbelasting.',
  AFGEREKEND: 'Gekoppeld en afgerekend met de doorbelasting uit de intercompany-engine.',
  VERSCHIL: 'De bedragen sluiten niet aan, of een gekoppelde factuur is vervallen.',
}

export const INTERNE_GELDSTROOM_STATUS_BADGE: Record<
  InterneGeldstroomStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  OPEN: 'warning',
  GEKOPPELD: 'info',
  AFGEREKEND: 'success',
  VERSCHIL: 'danger',
}

export const INTERNE_GELDSTROOM_OORSPRONG = ['ENGINE', 'EXTERN_GEMAAKT', 'MAIL_IMPORT'] as const
export type InterneGeldstroomOorsprong = (typeof INTERNE_GELDSTROOM_OORSPRONG)[number]

export const INTERNE_GELDSTROOM_OORSPRONG_LABELS: Record<InterneGeldstroomOorsprong, string> = {
  ENGINE: 'Intercompany-engine',
  EXTERN_GEMAAKT: 'Buiten de app gemaakt',
  MAIL_IMPORT: 'Mailimport',
}

/** Waarom een geldstroom op VERSCHIL staat. */
export const INTERNE_GELDSTROOM_VERSCHIL_REDEN = [
  'ENGINE_BEDRAG',
  'ENGINE_VERVALLEN',
  'ENGINE_GECORRIGEERD',
  'SPIEGEL_BEDRAG',
  'INKOOPFACTUUR_VERVALLEN',
  'VERKOOPFACTUUR_VERVALLEN',
  'BV_GEWIJZIGD',
] as const
export type InterneGeldstroomVerschilReden = (typeof INTERNE_GELDSTROOM_VERSCHIL_REDEN)[number]

export const INTERNE_GELDSTROOM_VERSCHIL_REDEN_LABELS: Record<InterneGeldstroomVerschilReden, string> = {
  ENGINE_BEDRAG: 'het bedrag van de doorbelasting wijkt af van de factuur',
  ENGINE_VERVALLEN:
    'een afgerekende doorbelasting is in de engine vervallen (ingetrokken, afgekeurd of opnieuw te berekenen)',
  ENGINE_GECORRIGEERD:
    'een afgerekende doorbelasting is in de engine gecorrigeerd en de correctie is nog niet afgerekend',
  SPIEGEL_BEDRAG: 'de verkoopfactuur heeft een ander bedrag dan de inkoopfactuur',
  INKOOPFACTUUR_VERVALLEN: 'de inkoopfactuur is geannuleerd, verplaatst of verwijderd',
  VERKOOPFACTUUR_VERVALLEN: 'de verkoopfactuur is verwijderd',
  BV_GEWIJZIGD: 'de BV op een gekoppelde factuur is na het koppelen gewijzigd',
}

/**
 * Een bedragverschil is met reden te accepteren; een vervallen factuur, een
 * vervallen doorbelasting of een nog niet afgerekende engine-correctie niet (die
 * los je op: herstellen, ontkoppelen of de doorbelastingen opnieuw kiezen).
 */
export const INTERNE_GELDSTROOM_ACCEPTEERBARE_REDENEN: readonly InterneGeldstroomVerschilReden[] = [
  'ENGINE_BEDRAG',
  'SPIEGEL_BEDRAG',
]

/** Leesbare opsomming van de verschilredenen ("a en b"). */
export function beschrijfGeldstroomVerschil(redenen: readonly InterneGeldstroomVerschilReden[]): string {
  const teksten = redenen.map((r) => INTERNE_GELDSTROOM_VERSCHIL_REDEN_LABELS[r])
  if (teksten.length === 0) return 'de bedragen sluiten niet aan'
  if (teksten.length === 1) return teksten[0]!
  return `${teksten.slice(0, -1).join(', ')} en ${teksten[teksten.length - 1]}`
}

// ─── Matcher-uitkomsten ──────────────────────────────────────────────────────

/** Aantal dagen dat de spiegel-verkoopfactuur van de factuurdatum mag afwijken. */
export const INTERNE_SPIEGEL_DATUM_TOLERANTIE_DAGEN = 3

/**
 * Hoe sterk een verkoopfactuur op de inkoopfactuur lijkt.
 *   STERK          - nummer, bedrag en datum (plus of min 3 dagen) kloppen alle drie;
 *   WAARSCHIJNLIJK - twee van de drie;
 *   ZWAK           - één van de drie.
 * Alleen een enkele STERKE kandidaat wordt automatisch gepaard (bulk, opschoning).
 */
export const INTERNE_SPIEGEL_STERKTE = ['STERK', 'WAARSCHIJNLIJK', 'ZWAK'] as const
export type InterneSpiegelSterkte = (typeof INTERNE_SPIEGEL_STERKTE)[number]

export const INTERNE_SPIEGEL_STERKTE_LABELS: Record<InterneSpiegelSterkte, string> = {
  STERK: 'Sterke match',
  WAARSCHIJNLIJK: 'Waarschijnlijke match',
  ZWAK: 'Zwakke match',
}

/** Waar het tegenpartij-voorstel vandaan komt. */
export const INTERNE_TEGENPARTIJ_BRON = ['MARKERING', 'INTERNE_LEVERANCIER', 'HERKEND'] as const
export type InterneTegenpartijBron = (typeof INTERNE_TEGENPARTIJ_BRON)[number]

export const INTERNE_TEGENPARTIJ_BRON_LABELS: Record<InterneTegenpartijBron, string> = {
  MARKERING: 'uit de intercompany-markering op de factuur',
  INTERNE_LEVERANCIER: 'de leverancier is de interne relatie van deze BV',
  HERKEND: 'herkend op de naam of het KvK-nummer van de leverancier',
}

/** Hoe de spiegel bij het koppelen wordt gevuld. */
export const INTERNE_SPIEGEL_MODUS = ['BESTAAND', 'REGISTREREN', 'GEEN'] as const
export type InterneSpiegelModus = (typeof INTERNE_SPIEGEL_MODUS)[number]

/** Inkoopstatussen waarin een factuur aan de geldstroom te koppelen is (geboekt). */
export const INTERNE_GELDSTROOM_KOPPELBARE_INKOOP_STATUSSEN = ['GOEDGEKEURD', 'BETAALD'] as const

/** Waarom een inkoopfactuur (nu) niet te koppelen is. */
export const INTERNE_GELDSTROOM_BLOKKADE = [
  'VERPLAATST_NAAR_VERKOOP',
  'NIET_GEBOEKT',
  'GEEN_ONTVANGENDE_BV',
  'AL_GEKOPPELD',
  'VERWIJDERD',
] as const
export type InterneGeldstroomBlokkade = (typeof INTERNE_GELDSTROOM_BLOKKADE)[number]

export const INTERNE_GELDSTROOM_BLOKKADE_MELDING: Record<InterneGeldstroomBlokkade, string> = {
  VERPLAATST_NAAR_VERKOOP:
    'Deze factuur is als verkoopfactuur geboekt en is vanaf de inkoopzijde niet meer te koppelen.',
  NIET_GEBOEKT:
    'Boek de factuur eerst (goedkeuren). Daarna kan hij aan de interne geldstroom gekoppeld worden.',
  GEEN_ONTVANGENDE_BV: 'Kies eerst de ontvangende BV van deze inkoopfactuur.',
  AL_GEKOPPELD: 'Deze factuur hangt al aan een interne geldstroom.',
  VERWIJDERD: 'Deze factuur is verwijderd.',
}

/** Uitkomst per rij van de bulk-koppelactie en de opschoning. */
export const INTERNE_GELDSTROOM_BULK_UITKOMST = [
  'GEKOPPELD',
  'SPIEGEL_GEREGISTREERD',
  'HANDMATIG',
  'NIET_KOPPELBAAR',
  'FOUT',
] as const
export type InterneGeldstroomBulkUitkomst = (typeof INTERNE_GELDSTROOM_BULK_UITKOMST)[number]

/** Waarom een rij niet automatisch gepaard kon worden. */
export const INTERNE_GELDSTROOM_HANDMATIG_REDEN = [
  'GEEN_TEGENPARTIJ',
  'SPIEGEL_ONTBREEKT',
  'MEERDERE_KANDIDATEN',
  'GEEN_STERKE_MATCH',
  'DEBITEUR_WIJKT_AF',
  'CREDIT_ZONDER_ORIGINEEL',
  'SPIEGELNUMMER_BEZET',
  'SPIEGELNUMMER_IN_REEKS',
] as const
export type InterneGeldstroomHandmatigReden = (typeof INTERNE_GELDSTROOM_HANDMATIG_REDEN)[number]

export const INTERNE_GELDSTROOM_HANDMATIG_REDEN_LABELS: Record<InterneGeldstroomHandmatigReden, string> = {
  GEEN_TEGENPARTIJ: 'De verzendende BV is niet eenduidig te bepalen',
  SPIEGEL_ONTBREEKT: 'Er staat geen spiegel-verkoopfactuur in de app; registreer de spiegel',
  MEERDERE_KANDIDATEN: 'Meer dan één verkoopfactuur past even goed; kies de juiste',
  GEEN_STERKE_MATCH: 'Geen verkoopfactuur waarvan nummer, bedrag en datum alle drie kloppen',
  DEBITEUR_WIJKT_AF:
    'Nummer, bedrag en datum kloppen, maar de debiteur van de verkoopfactuur is niet herkend als de ontvangende BV; controleer en koppel zelf',
  CREDIT_ZONDER_ORIGINEEL: 'Creditnota: de oorspronkelijke geldstroom is niet eenduidig',
  SPIEGELNUMMER_BEZET: 'Het factuurnummer is bij de verzendende BV al in gebruik',
  SPIEGELNUMMER_IN_REEKS:
    'Het factuurnummer valt in de factuurreeks; boek de verkoopfactuur via de gewone import en koppel hem daarna',
}

// ─── Request-schema's (API + form) ───────────────────────────────────────────

const PERIODE_RE = /^\d{4}-(0[1-9]|1[0-2])$/

/** Maandperiode `YYYY-MM` van een interne geldstroom. */
export const interneGeldstroomPeriodeSchema = z
  .string({ error: 'Kies de periode' })
  .regex(PERIODE_RE, 'Kies een geldige periode (maand en jaar)')

const optioneleTekst = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Maximaal ${max} tekens`)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null))

/**
 * Koppelen van een inkoopfactuur aan de interne geldstroom
 * (`POST /inkoop/facturen/:id/geldstroom`).
 */
export const interneGeldstroomKoppelSchema = z
  .object({
    /** De verzendende (factuurende) BV. */
    bronEntityId: z.string({ error: 'Kies de verzendende BV' }).min(1, 'Kies de verzendende BV'),
    periode: interneGeldstroomPeriodeSchema,
    spiegelModus: z.enum(INTERNE_SPIEGEL_MODUS, { error: 'Kies wat er met de spiegel-verkoopfactuur gebeurt' }),
    /** Verplicht bij `spiegelModus = BESTAAND`. */
    verkoopInvoiceId: z.string().min(1).nullable().optional(),
    /** De engine-doorbelastingen die met deze factuur zijn afgerekend. */
    engineTransactieIds: z.array(z.string().min(1)).max(100, 'Maximaal 100 doorbelastingen').default([]),
    /** Creditnota: de geldstroom van de oorspronkelijke factuur. */
    origineelGeldstroomId: z.string().min(1).nullable().optional(),
    /** Creditnota waarvan het origineel niet in de app staat: verantwoording. */
    zonderOrigineelReden: optioneleTekst(500),
    toelichting: optioneleTekst(500),
  })
  .superRefine((v, ctx) => {
    if (v.spiegelModus === 'BESTAAND' && !v.verkoopInvoiceId) {
      ctx.addIssue({
        code: 'custom',
        path: ['verkoopInvoiceId'],
        message: 'Kies de spiegel-verkoopfactuur',
      })
    }
    if (v.spiegelModus !== 'BESTAAND' && v.verkoopInvoiceId) {
      ctx.addIssue({
        code: 'custom',
        path: ['verkoopInvoiceId'],
        message: 'Een verkoopfactuur kiezen kan alleen bij "bestaande spiegel koppelen"',
      })
    }
    if (v.origineelGeldstroomId && v.zonderOrigineelReden) {
      ctx.addIssue({
        code: 'custom',
        path: ['zonderOrigineelReden'],
        message: 'Kies de oorspronkelijke geldstroom of geef een reden, niet allebei',
      })
    }
  })
export type InterneGeldstroomKoppelInput = z.infer<typeof interneGeldstroomKoppelSchema>

/** De spiegel alsnog vullen op een eenzijdige geldstroom. */
export const interneGeldstroomSpiegelSchema = z
  .object({
    spiegelModus: z.enum(['BESTAAND', 'REGISTREREN'], { error: 'Kies een bestaande verkoopfactuur of registreer de spiegel' }),
    verkoopInvoiceId: z.string().min(1).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.spiegelModus === 'BESTAAND' && !v.verkoopInvoiceId) {
      ctx.addIssue({
        code: 'custom',
        path: ['verkoopInvoiceId'],
        message: 'Kies de spiegel-verkoopfactuur',
      })
    }
  })
export type InterneGeldstroomSpiegelInput = z.infer<typeof interneGeldstroomSpiegelSchema>

/** De engine-doorbelastingen van een geldstroom zetten (vervangt de selectie). */
export const interneGeldstroomEngineSchema = z.object({
  engineTransactieIds: z.array(z.string().min(1)).max(100, 'Maximaal 100 doorbelastingen'),
  /** Optioneel: de periode mee verzetten (de doorbelastingen horen bij een maand). */
  periode: interneGeldstroomPeriodeSchema.optional(),
})
export type InterneGeldstroomEngineInput = z.infer<typeof interneGeldstroomEngineSchema>

/** Ontkoppelen: altijd met reden (audit). */
export const interneGeldstroomOntkoppelSchema = z.object({
  reden: z
    .string({ error: 'Geef een reden voor het ontkoppelen' })
    .trim()
    .min(3, 'Geef een reden voor het ontkoppelen (minimaal 3 tekens)')
    .max(500, 'Maximaal 500 tekens'),
})
export type InterneGeldstroomOntkoppelInput = z.infer<typeof interneGeldstroomOntkoppelSchema>

const verschilBedrag = z
  .string({ error: 'Het verschil ontbreekt; vernieuw de pagina' })
  .regex(/^-?\d+\.\d{2}$/, 'Het verschil is geen geldig bedrag; vernieuw de pagina')
  .nullable()

/**
 * Een bedragverschil bewust accepteren: altijd met reden (audit). Wat de
 * gebruiker op het scherm ZAG reist mee: het verschil (`engineVerschil` /
 * `spiegelVerschil`, null = die kant had geen verschil) en de doorbelastingen
 * (`engineTransactieIds`, leeg = geen engine-koppeling). Is een van beide
 * intussen gewijzigd, dan weigert de API (409) in plaats van iets anders te
 * accepteren dan beoordeeld is.
 *
 * De doorbelastingen horen erbij omdat een acceptatie geldt voor de
 * doorbelastingen waarvoor zij is gegeven: een ander stel met toevallig
 * hetzelfde verschil is een ander oordeel. Om dezelfde reden vervalt een
 * gegeven acceptatie zodra de doorbelastingen daarna wisselen.
 */
export const interneGeldstroomVerschilAccepterenSchema = z.object({
  reden: z
    .string({ error: 'Geef een reden voor het accepteren van het verschil' })
    .trim()
    .min(3, 'Geef een reden voor het accepteren van het verschil (minimaal 3 tekens)')
    .max(500, 'Maximaal 500 tekens'),
  engineVerschil: verschilBedrag,
  spiegelVerschil: verschilBedrag,
  engineTransactieIds: z
    .array(z.string().min(1), { error: 'De doorbelastingen ontbreken; vernieuw de pagina' })
    .max(100, 'Maximaal 100 doorbelastingen'),
})
export type InterneGeldstroomVerschilAccepterenInput = z.infer<
  typeof interneGeldstroomVerschilAccepterenSchema
>

/** Bulk-koppelen vanuit de inkooplijst (filter intercompany). */
export const interneGeldstroomBulkSchema = z.object({
  ids: z
    .array(z.string().min(1))
    .min(1, 'Selecteer minimaal één factuur')
    .max(200, 'Maximaal 200 facturen per keer'),
  /** Alleen beoordelen (voorvertoning); er wordt niets geschreven. */
  dryRun: z.boolean().default(false),
  /**
   * Ontbreekt de spiegel volledig (geen enkele kandidaat), registreer hem dan
   * als IMPORT-verkoopfactuur. Vereist FACTUREN.canEdit; default uit.
   */
  registreerOntbrekendeSpiegels: z.boolean().default(false),
})
export type InterneGeldstroomBulkInput = z.infer<typeof interneGeldstroomBulkSchema>

// ── Query-schema's (lijsten + voorstel) ──────────────────────────────────────

const paginaVelden = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
}

/** `GET /intercompany/geldstromen`. */
export const interneGeldstroomListQuerySchema = z.object({
  status: z.enum(INTERNE_GELDSTROOM_STATUS).optional(),
  bronEntityId: z.string().min(1).optional(),
  ontvangendeEntityId: z.string().min(1).optional(),
  periode: interneGeldstroomPeriodeSchema.optional(),
  ...paginaVelden,
})
export type InterneGeldstroomListQuery = z.infer<typeof interneGeldstroomListQuerySchema>

/** `GET /intercompany/geldstromen/ongekoppeld`: de opschoonlijst per kant. */
export const INTERNE_GELDSTROOM_ONGEKOPPELD_KANT = ['INKOOP', 'VERKOOP', 'ZONDER_SPIEGEL'] as const
export type InterneGeldstroomOngekoppeldKant = (typeof INTERNE_GELDSTROOM_ONGEKOPPELD_KANT)[number]

export const interneGeldstroomOngekoppeldQuerySchema = z.object({
  kant: z.enum(INTERNE_GELDSTROOM_ONGEKOPPELD_KANT).default('INKOOP'),
  ...paginaVelden,
})
export type InterneGeldstroomOngekoppeldQuery = z.infer<typeof interneGeldstroomOngekoppeldQuerySchema>

/** `GET /intercompany/geldstromen/overzicht`: leeg = de lopende maand. */
export const interneGeldstroomOverzichtQuerySchema = z.object({
  periode: interneGeldstroomPeriodeSchema.optional(),
})

/** `GET /inkoop/facturen/:id/geldstroom/voorstel`: optioneel een andere BV of periode. */
export const interneGeldstroomVoorstelQuerySchema = z.object({
  bronEntityId: z.string().min(1).optional(),
  periode: interneGeldstroomPeriodeSchema.optional(),
})

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface InterneGeldstroomBvRefDTO {
  id: string
  name: string
}

export interface InterneGeldstroomInkoopRefDTO {
  id: string
  factuurNr: string
  status: string
  factuurdatum: string
  bedragExcl: string
  bedragIncl: string
}

export interface InterneGeldstroomVerkoopRefDTO {
  id: string
  invoiceNumber: string | null
  status: string
  invoiceDate: string | null
  bedragExcl: string
  bedragIncl: string
  /** Door "spiegel registreren" aangemaakt (buiten de factuurreeks). */
  geregistreerd: boolean
  verwijderd: boolean
}

export interface InterneGeldstroomEngineTxDTO {
  id: string
  code: string
  type: string
  status: string
  periodeLabel: string
  /** Doorbelast bedrag excl. btw (subtotaal + opslag). */
  bedragExcl: string
  /** In de engine ingetrokken of verwijderd terwijl hij nog aan de geldstroom hangt. */
  verwijderd: boolean
}

/**
 * Compacte verwijzing op een factuur-DTO (inkoop én verkoop): "deze factuur
 * hangt aan geldstroom X, met tegenhanger Y". De wederzijdse verwijzing.
 */
export interface InterneGeldstroomRefDTO {
  id: string
  status: InterneGeldstroomStatus
  oorsprong: InterneGeldstroomOorsprong
  periode: string
  bron: InterneGeldstroomBvRefDTO
  ontvanger: InterneGeldstroomBvRefDTO
  inkoopFactuur: { id: string; factuurNr: string } | null
  verkoopInvoice: { id: string; invoiceNumber: string | null } | null
  spiegelGeregistreerd: boolean
  /**
   * Het koppelen zette de intercompany-markering op die factuur (hij was
   * daarvoor niet gemarkeerd); ontkoppelen zet hem terug. Voor een geregistreerde
   * spiegel altijd false: die gaat bij ontkoppelen weg.
   */
  markeringDoorKoppeling: { inkoop: boolean; verkoop: boolean }
  /**
   * De factuur was al gemarkeerd, maar het koppelen zette er een andere
   * tegenpartij-BV op; ontkoppelen zet de vorige terug.
   */
  tegenpartijDoorKoppeling: { inkoop: boolean; verkoop: boolean }
  /** Aantal afgerekende engine-doorbelastingen. */
  engineAantal: number
  verschilRedenen: InterneGeldstroomVerschilReden[]
  /**
   * Per deel: is dit verschil bewust geaccepteerd (en klopt het nog)? `engine` =
   * het verschil met de doorbelastingen, `spiegel` = het verschil tussen de twee
   * facturen. De acceptatie van het engine-deel vervalt zodra de doorbelastingen
   * wisselen; het scherm toont alleen het deel dat echt geaccepteerd is.
   */
  verschilGeaccepteerdDelen: { engine: boolean; spiegel: boolean }
  /**
   * Het engine-verschil waarvoor op de huidige doorbelastingen een acceptatie is
   * VASTGELEGD, of null. Kan afwijken van het actuele verschil: dan geldt de
   * acceptatie nu niet (zie `verschilGeaccepteerdDelen.engine`), maar zij staat
   * er nog en vervalt zodra de doorbelastingen wisselen. De dialoog
   * "Doorbelastingen kiezen" waarschuwt daar vooraf voor.
   */
  vastgelegdEngineVerschil: string | null
}

/** Volledige rij (lijst + detail). */
export interface InterneGeldstroomDTO extends Omit<InterneGeldstroomRefDTO, 'inkoopFactuur' | 'verkoopInvoice'> {
  periodeLabel: string
  bedragExcl: string
  btwBedrag: string
  bedragIncl: string
  inkoopFactuur: InterneGeldstroomInkoopRefDTO | null
  verkoopInvoice: InterneGeldstroomVerkoopRefDTO | null
  /** Som excl. btw van de afgerekende doorbelastingen; null zonder engine-koppeling. */
  engineBedragExcl: string | null
  /** Engine min factuur (excl. btw); null zonder engine-koppeling. */
  engineVerschil: string | null
  /** Verkoop min inkoop (excl. btw); null zolang een kant ontbreekt. */
  spiegelVerschil: string | null
  /** Een deel van het actuele verschil is bewust geaccepteerd (zie `verschilGeaccepteerdDelen`). */
  verschilGeaccepteerd: boolean
  verschilGeaccepteerdReden: string | null
  verschilGeaccepteerdOp: string | null
  verschilGeaccepteerdDoor: string | null
  /** Creditnota: de oorspronkelijke geldstroom. */
  origineel: { id: string; factuurNr: string | null; periode: string } | null
  zonderOrigineelReden: string | null
  isCredit: boolean
  gekoppeldOp: string
  gekoppeldDoor: string | null
}

export interface InterneGeldstroomAuditDTO {
  id: string
  actie: string
  actor: string | null
  toelichting: string | null
  createdAt: string
}

export interface InterneGeldstroomDetailDTO extends InterneGeldstroomDTO {
  transacties: InterneGeldstroomEngineTxDTO[]
  creditnotas: { id: string; factuurNr: string | null; bedragExcl: string; periode: string }[]
  /** De laatste auditregels (nieuwste eerst), begrensd; `auditTotaal` is het echte aantal. */
  audits: InterneGeldstroomAuditDTO[]
  auditTotaal: number
}

export interface InterneGeldstroomListMeta {
  total: number
  page: number
  pageSize: number
  perStatus: Record<InterneGeldstroomStatus, number>
}

// ── Voorstel (koppel-dialoog) ────────────────────────────────────────────────

export interface InterneSpiegelKandidaatDTO {
  id: string
  invoiceNumber: string | null
  invoiceDate: string | null
  status: string
  klantNaam: string | null
  bedragExcl: string
  bedragIncl: string
  nummerGelijk: boolean
  bedragGelijk: boolean
  datumBinnenTolerantie: boolean
  /** Dagen tussen de twee factuurdatums; null als de verkoopfactuur geen datum heeft. */
  dagenVerschil: number | null
  /** De debiteur van de verkoopfactuur is de ontvangende BV. */
  debiteurIsOntvanger: boolean
  sterkte: InterneSpiegelSterkte
}

export interface InterneEngineKandidaatDTO extends InterneGeldstroomEngineTxDTO {
  /** Door de matcher voorgeselecteerd (bedrag sluit eenduidig aan), of al gekoppeld. */
  voorgeselecteerd: boolean
  /** Hangt al aan de bestaande geldstroom van deze factuur. */
  gekoppeld: boolean
  /**
   * Sleutel van de correctieketen waar deze doorbelasting bij hoort (een
   * gecorrigeerd origineel, zijn credit en de nieuwe debet), of null. Een keten
   * wordt als geheel afgerekend: kies je er een, dan horen de andere erbij.
   */
  keten: string | null
  /** De code van de doorbelasting die deze corrigeert, of null. */
  correctieVan: string | null
}

export interface InterneOrigineelKandidaatDTO {
  geldstroomId: string
  factuurNr: string | null
  periode: string
  bedragExcl: string
  /** Het nummer van het origineel staat in de referentie/omschrijving van de credit. */
  nummerGenoemd: boolean
  /** Het bedrag is precies het tegengestelde van de creditnota. */
  bedragTegengesteld: boolean
}

export interface InterneGeldstroomVoorstelDTO {
  inkoopFactuur: InterneGeldstroomInkoopRefDTO & { isCredit: boolean }
  koppelbaar: boolean
  blokkade: { code: InterneGeldstroomBlokkade; melding: string } | null
  /** Bestaat er al een geldstroom op deze factuur, dan staat hij hier. */
  bestaandeGeldstroom: InterneGeldstroomRefDTO | null
  ontvanger: InterneGeldstroomBvRefDTO | null
  tegenpartij: {
    entity: InterneGeldstroomBvRefDTO | null
    bron: InterneTegenpartijBron | null
    toelichting: string
  }
  periode: string
  periodeLabel: string
  oorsprong: InterneGeldstroomOorsprong
  spiegelKandidaten: InterneSpiegelKandidaatDTO[]
  /** De ene STERKE kandidaat, als die er precies één is. */
  spiegelEenduidigId: string | null
  /** Kan de spiegel geregistreerd worden (nummer vrij bij de verzendende BV)? */
  spiegelRegistreren: { kan: boolean; reden: string | null }
  engine: {
    transacties: InterneEngineKandidaatDTO[]
    /** Som excl. btw van alle getoonde doorbelastingen. */
    totaalExcl: string
    /** Doorbelastingen van dit paar in deze periode die nog geen bedrag hebben. */
    nogNietBerekend: number
    /** Hint: niet-afgerekende doorbelastingen in de maand ervoor. */
    vorigePeriode: { periode: string; periodeLabel: string; aantal: number } | null
  }
  origineel: {
    /** Alleen bij een creditnota. */
    vanToepassing: boolean
    kandidaten: InterneOrigineelKandidaatDTO[]
    eenduidigId: string | null
  }
}

// ── Bulk + opschoning ────────────────────────────────────────────────────────

export interface InterneGeldstroomBulkRijDTO {
  inkoopFactuurId: string
  factuurNr: string
  uitkomst: InterneGeldstroomBulkUitkomst
  /** Bij HANDMATIG. */
  handmatigReden: InterneGeldstroomHandmatigReden | null
  melding: string
  geldstroomId: string | null
  status: InterneGeldstroomStatus | null
  spiegelInvoiceNumber: string | null
  /** De periode waar de geldstroom op komt; null zolang er niets gekoppeld wordt. */
  periode: string | null
  /**
   * De doorbelastingen die met deze factuur (worden) afgerekend. Alleen gevuld
   * wanneer de periode VASTSTAAT (de factuur komt uit een herhaal-regel) en het
   * bedrag eenduidig aansluit; een uit de factuurdatum afgeleide periode rekent
   * nooit automatisch af.
   */
  engineTransacties: { code: string; bedragExcl: string }[]
  /** Er staan doorbelastingen open die een mens op de geldstroom moet afrekenen. */
  engineHandmatig: boolean
}

export interface InterneGeldstroomBulkResultaatDTO {
  dryRun: boolean
  rijen: InterneGeldstroomBulkRijDTO[]
  samenvatting: {
    gekoppeld: number
    spiegelGeregistreerd: number
    handmatig: number
    nietKoppelbaar: number
    fout: number
    /** Rijen waarbij ook de doorbelasting(en) uit de engine (worden) afgerekend. */
    engineAfgerekend: number
    /** Rijen met openstaande doorbelastingen die een mens op de geldstroom afrekent. */
    engineHandmatig: number
  }
}

/**
 * Een eenzijdig geboekte interne factuur (de opschoonlijst): een
 * intercompany-factuur zonder geldstroom (`INKOOP` / `VERKOOP`), of een
 * inkoopfactuur die al gekoppeld is maar nog geen spiegel heeft (`ZONDER_SPIEGEL`).
 */
export interface InterneGeldstroomOngekoppeldDTO {
  kant: InterneGeldstroomOngekoppeldKant
  /** Bij `ZONDER_SPIEGEL`: de geldstroom waar de spiegel nog op ontbreekt. */
  geldstroomId: string | null
  id: string
  nummer: string | null
  datum: string | null
  status: string
  bedragExcl: string
  bedragIncl: string
  bron: InterneGeldstroomBvRefDTO | null
  ontvanger: InterneGeldstroomBvRefDTO | null
}

export interface InterneGeldstroomOngekoppeldMeta {
  total: number
  page: number
  pageSize: number
  inkoopZonderGeldstroom: number
  verkoopZonderGeldstroom: number
  geldstroomZonderSpiegel: number
}

/** Het resultaat van ontkoppelen (`POST /intercompany/geldstromen/:id/ontkoppelen`). */
export interface InterneGeldstroomOntkoppelResultaatDTO {
  geldstroomId: string
  /** De geregistreerde spiegel is mee verwijderd (herstelbaar, nummer vrijgegeven). */
  spiegelVerwijderd: boolean
  /** De geregistreerde spiegel is al verder verwerkt en blijft als verkoopfactuur staan. */
  spiegelBehouden: boolean
  /**
   * De intercompany-markering die het koppelen zelf zette, is teruggezet: de
   * factuur telt weer mee in de externe cijfers (en de inkoopfactuur op de
   * betaallijst), precies zoals voor het koppelen.
   */
  markeringTeruggezet: { inkoop: boolean; verkoop: boolean }
  /**
   * De factuur blijft gemarkeerd, maar de tegenpartij-BV van voor het koppelen
   * is teruggezet (het koppelen had er een andere BV op gezet).
   */
  tegenpartijTeruggezet: { inkoop: boolean; verkoop: boolean }
}

// ── Periodeoverzicht per BV-paar ─────────────────────────────────────────────

export interface InterneGeldstroomPaarDTO {
  bron: InterneGeldstroomBvRefDTO
  ontvanger: InterneGeldstroomBvRefDTO
  /** Som excl. btw van de berekende engine-doorbelastingen in de periode. */
  engineSaldo: string
  engineAantal: number
  /** Doorbelastingen zonder bedrag (nog te berekenen / wacht / geblokkeerd). */
  engineNogNietBerekend: number
  /** Som excl. btw van de geldstromen (de interne facturen) in de periode. */
  gefactureerd: string
  geldstroomAantal: number
  /** `engineSaldo - gefactureerd`. */
  verschil: string
  perStatus: Record<InterneGeldstroomStatus, number>
  /** Eenzijdig: intercompany-inkoopfacturen zonder geldstroom of zonder spiegel. */
  inkoopZonderVerkoop: number
  /** Eenzijdig: intercompany-verkoopfacturen zonder geldstroom. */
  verkoopZonderInkoop: number
}

export interface InterneGeldstroomOverzichtDTO {
  periode: string
  periodeLabel: string
  paren: InterneGeldstroomPaarDTO[]
  totalen: {
    engineSaldo: string
    gefactureerd: string
    verschil: string
    geldstroomAantal: number
    inkoopZonderVerkoop: number
    verkoopZonderInkoop: number
    /**
     * Eenzijdige facturen van deze maand zonder bekende tegenpartij-BV: ze tellen
     * mee in de twee tellers hierboven, maar staan bij geen enkel BV-paar.
     */
    zonderTegenpartij: number
    perStatus: Record<InterneGeldstroomStatus, number>
  }
  peilmoment: string
}

// ─── Zichtbare copy (gedeeld door UI en meldingen) ───────────────────────────

export const INTERNE_GELDSTROOM_LABEL = 'Interne geldstroom'
export const INTERNE_GELDSTROOM_KOPPEL_KNOP = 'Koppelen aan interne geldstroom'
export const INTERNE_GELDSTROOM_SPIEGEL_REGISTREREN_UITLEG =
  'De spiegel wordt bij de verzendende BV als verkoopfactuur geregistreerd, met hetzelfde nummer, bedrag en dezelfde datum. Hij staat buiten de factuurreeks: er wordt geen factuurnummer verbruikt en er ontstaat geen gat.'
